import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Link } from 'react-router'
import { withTranslation, useTranslation } from 'react-i18next'

import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import IconFlag from 'components/Shared/IconFlag/Index'
import CopyIcon from 'components/Shared/CopyIcon/Index'
import colors from 'components/utils/colors'

const IconsWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 10px;
  right: 13px;
  gap: 10px;
  transition: all .15s linear;
  opacity: 0;
`

const StyledSection = styled.section`
  border-bottom: 1px solid ${colors.secondary70};
  position: relative;
  transition: all .15s linear;
  &:hover {
    background: ${colors.primary100};
    ${IconsWrapper} {
      opacity: 1;
    }
  }
  padding: ${props => !props.noPaddings && css`
    12px 16px 16px 16px;
  `}
`

const ExpandContentIcon = styled(IconLink)`
`

const StyledLinkToIcon = styled(IconLink)`
`

const StyledToolTipIcon = styled(IconLink)`
  margin-left: 5px;
`

const StyledIconFlag = styled.div`
  margin-left: 5px;
`

const StyledTitleSection = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
`

const StyledTitleText = styled.div`
  font-family: Exo;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.secondary60};
  letter-spacing: 0.3px;
  text-transform: uppercase;

  ${props => props.isSubHeader && css`
    color: ${colors.secondary20};
    text-transform: uppercase;
    padding-top: 4px;
    font-size: 14px;
    font-weight: 500;
  `}
`

const Spacer = styled.div`
  height: 12px;
`

const StyledRequiredIcon = styled.div`
  background-color: ${colors.alert};
  border-radius: 50%;
  width: 8px;
  min-width: 8px;
  height: 8px;
  margin-left: 4px;
`


const SecureIcon = ({ visibility, tooltipPlace = 'top', onClick }) => {
  const { t } = useTranslation()
  const TooltipText = visibility ? t('common.hideSecureInfo') : t('common.viewSecureInfo')
  const IconName = visibility ? 'icon-hidden' : 'icon-visible'

  return (
    <>
      <Tooltip
        id='trigger_secure_column'
        place={tooltipPlace}
        closeEvents={{ mouseleave: true, click: true }}
      />

      <IconLink
        iconName={IconName}
        onClick={onClick}
        data-tooltip-content={TooltipText}
        data-tooltip-id='trigger_secure_column'
      />
    </>
  )
}

SecureIcon.propTypes = {
  visibility: PropTypes.bool,
  onClick: PropTypes.func,
  tooltipPlace: PropTypes.string,
}

const SidebarSection = ({
  children, title, copyText, tooltipText, flagName, isRequired, expandedContent,
  onExpandContent, linkTo, linkTooltipText, noPaddings, t,
  secureIconState, showSecureIcon, secureIconOnClick,
}) => {
  const showLinkIcon = !!linkTo || linkTo === 0
  const showExpandContentIcon = !!expandedContent


  // it is used in Audit Filters (left sidebar) only
  // TODO: consider adding separate component as it is not clear
  // and hard to find usage of components without children
  const isSubHeader = !children

  return (
    <StyledSection
      isRequired={isRequired}
      noPaddings={noPaddings}
      data-test='sidebar-section'
    >
      {title && (
        <StyledTitleSection
          data-tooltip-content={tooltipText || undefined}
          data-tooltip-id='sidebar-left-tooltip'
        >
          <StyledTitleText isSubHeader={isSubHeader} data-test='sidebar-section-title'>{title}</StyledTitleText>
          {isRequired && <StyledRequiredIcon />}
          {tooltipText && (
            <StyledToolTipIcon
              iconName='icon-help-12'
            />
          )}

          {flagName && (
            <StyledIconFlag>
              <IconFlag flagName={flagName} isSmallFlag />
            </StyledIconFlag>
          )}
        </StyledTitleSection>
      )}

      <IconsWrapper>
        <CopyIcon copyText={copyText} />
        {showExpandContentIcon && (
          <ExpandContentIcon
            onClick={onExpandContent}
            iconName='icon-more'
            data-tooltip-id='sidebar-left-link-tooltip'
            data-tooltip-content={t('common.tooltipShowMore')}
          />
        )}
        {showLinkIcon && (
          <Link to={linkTo}>
            <StyledLinkToIcon
              iconName='icon-export'
              data-tooltip-id='sidebar-left-link-tooltip'
              data-tooltip-content={linkTooltipText}
            />
          </Link>
        )}
        {showSecureIcon && (
          <SecureIcon
            visibility={secureIconState}
            onClick={secureIconOnClick}
          />
        )}

      </IconsWrapper>

      {title && !isSubHeader && <Spacer />}
      {children}
      <Tooltip id='sidebar-left-tooltip' place='left' delayHide={500} />
      <Tooltip id='sidebar-left-link-tooltip' place='left' />
    </StyledSection>
  )
}

SidebarSection.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  copyText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipText: PropTypes.string,
  flagName: PropTypes.string,
  isRequired: PropTypes.bool,
  showSecureIcon: PropTypes.bool,
  secureIconState: PropTypes.bool,
  expandedContent: PropTypes.string,
  onExpandContent: PropTypes.func,
  secureIconOnClick: PropTypes.func,
  linkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  linkTooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noPaddings: PropTypes.bool,
  t: PropTypes.func,
}

export default withTranslation()(SidebarSection)
